import { useNavigate } from "react-router-dom";
import { WindowsLogo, AppleLogo, LinuxLogo, Monitor, Trash, Plugs, DotsThreeVertical, DownloadSimple, ArrowsClockwise, CheckCircle, PencilSimple, Ticket, UserCircle } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSupportEnabled } from "@/hooks/useSupportEnabled";

function OsIcon({ os }) {
  const s = (os || "").toLowerCase();
  const props = { size: 22, weight: "fill", className: "text-zinc-300" };
  if (s.includes("win")) return <WindowsLogo {...props} />;
  if (s.includes("mac") || s.includes("darwin")) return <AppleLogo {...props} />;
  if (s.includes("linux")) return <LinuxLogo {...props} />;
  return <Monitor {...props} />;
}

// Compare dotted numeric versions: -1 if a<b, 0 if equal, 1 if a>b.
function cmpVersions(a, b) {
  const pa = String(a).split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d < 0 ? -1 : 1;
  }
  return 0;
}

export default function MachineCard({ machine, onDelete, onRename, onShowInstaller, onShowUpdate, onRegenerate, onCancelUninstall, onAssignCustomer, canManage = true, latestVersion }) {
  const navigate = useNavigate();
  const { enabled: supportEnabled } = useSupportEnabled();
  const online = machine.online;
  const pending = machine.pending_uninstall;
  const version = machine.agent_version;
  const outdated = version && latestVersion && cmpVersions(version, latestVersion) < 0;

  return (
    <div
      data-testid={`machine-card-${machine.machine_id}`}
      className="group relative bg-zinc-900 border border-white/10 rounded-lg p-5 transition-all duration-200 ease-in-out hover:-translate-y-1 hover:border-white/20 hover:shadow-lg hover:shadow-black/40"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-zinc-800 flex items-center justify-center">
            <OsIcon os={machine.os} />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm tracking-tight">{machine.name}</div>
            <div className="font-mono text-xs text-zinc-500 mt-0.5">{machine.os || "Not yet paired"}</div>
            {supportEnabled && machine.customer_name && (
              <div data-testid={`machine-customer-${machine.machine_id}`} className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono text-blue-300/80">
                <UserCircle size={11} weight="fill" /> {machine.customer_name}
              </div>
            )}
            {version && (
              outdated ? (
                <button
                  type="button"
                  data-testid={`agent-version-badge-${machine.machine_id}`}
                  onClick={() => (onShowUpdate || onShowInstaller)(machine)}
                  title={`Running agent v${version} · latest is v${latestVersion}. Click to update this machine remotely.`}
                  className="mt-1.5 inline-flex items-center gap-1 rounded-full text-[10px] font-mono px-1.5 py-0.5 border border-amber-500/40 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
                >
                  <ArrowsClockwise size={10} weight="bold" /> v{version} · Update
                </button>
              ) : (
                <span
                  data-testid={`agent-version-badge-${machine.machine_id}`}
                  title={`Agent v${version} · up to date`}
                  className="mt-1.5 inline-flex items-center gap-1 rounded-full text-[10px] font-mono px-1.5 py-0.5 border border-white/10 text-zinc-500 bg-white/5"
                >
                  <CheckCircle size={10} weight="fill" className="text-emerald-500/70" /> v{version}
                </span>
              )
            )}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`machine-menu-${machine.machine_id}`} className={`text-zinc-500 hover:text-white p-1 rounded transition-colors ${canManage ? "" : "hidden"}`}>
              <DotsThreeVertical size={20} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
            <DropdownMenuItem
              data-testid={`rename-machine-${machine.machine_id}`}
              onClick={() => onRename(machine)}
              className="cursor-pointer"
            >
              <PencilSimple size={16} className="mr-2" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid={`installer-machine-${machine.machine_id}`}
              onClick={() => onShowInstaller(machine)}
              className="cursor-pointer"
            >
              <DownloadSimple size={16} className="mr-2" /> Get installer
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid={`regenerate-machine-${machine.machine_id}`}
              onClick={() => onRegenerate(machine)}
              className="cursor-pointer"
            >
              <ArrowsClockwise size={16} className="mr-2" /> Regenerate token
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            {supportEnabled && (
              <DropdownMenuItem
                data-testid={`assign-customer-machine-${machine.machine_id}`}
                onClick={() => onAssignCustomer && onAssignCustomer(machine)}
                className="cursor-pointer"
              >
                <UserCircle size={16} className="mr-2" /> {machine.customer_id ? "Change customer" : "Assign to customer"}
              </DropdownMenuItem>
            )}
            {supportEnabled && (
              <DropdownMenuItem
                data-testid={`create-ticket-machine-${machine.machine_id}`}
                onClick={() => navigate(`/helpdesk/tickets?new=1&machine_id=${machine.machine_id}${machine.customer_id ? `&customer_id=${machine.customer_id}` : ""}`)}
                className="cursor-pointer"
              >
                <Ticket size={16} className="mr-2" /> Create support ticket
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              data-testid={`delete-machine-${machine.machine_id}`}
              onClick={() => onDelete(machine)}
              className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
            >
              <Trash size={16} className="mr-2" /> Remove machine
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-5 flex items-center justify-between">
        {pending ? (
          <span data-testid={`pending-uninstall-${machine.machine_id}`} className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium border text-amber-400 border-amber-500/30 bg-amber-500/10">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Uninstall pending
          </span>
        ) : (
          <span className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
            online
              ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
              : "text-red-400 border-red-500/30 bg-red-500/10"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
            {online ? "Online" : "Offline"}
          </span>
        )}
        <span className="font-mono text-xs text-zinc-500">{machine.ip || "N/A"}</span>
      </div>

      <button
        data-testid={`connect-machine-${machine.machine_id}`}
        disabled={!online || pending}
        onClick={() => navigate(`/session/${machine.machine_id}`)}
        className={`mt-4 w-full flex items-center justify-center gap-2 h-10 rounded-md text-sm font-medium transition-colors ${
          online && !pending
            ? "bg-blue-600 hover:bg-blue-500 text-white"
            : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
        }`}
      >
        <Plugs size={16} weight="bold" /> {pending ? "Uninstalling…" : online ? "Connect" : "Waiting for agent"}
      </button>

      {pending && canManage && (
        <button
          data-testid={`cancel-uninstall-${machine.machine_id}`}
          onClick={() => onCancelUninstall?.(machine)}
          className="mt-2 w-full flex items-center justify-center gap-2 h-9 rounded-md text-xs font-medium border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors"
        >
          Cancel scheduled uninstall
        </button>
      )}
    </div>
  );
}
